import * as React from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { Tooltip } from 'react-tooltip'
import * as Api from 'src/api'
import { type CommonError } from 'src/api'
import { Draft } from 'src/assets/icons/customIcons/Draft'
import { Plus } from 'src/assets/icons/customIcons/Plus'
import { CourseCompletedIcon } from 'src/assets/icons/customIcons/course-icons/CourseCompleted'
import { CourseFailedIcon } from 'src/assets/icons/customIcons/course-icons/CourseFailed'
import CoursePending from 'src/assets/icons/customIcons/course-icons/CoursePending'
import { errorsToArray } from 'src/helpers/fns'
import { useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import Warning from 'src/imgs/classroom_icon.svg'
import ErrorIcon from 'src/imgs/error_icon.svg'
import { useTheme } from 'src/state/providers/Theme'
import { Button } from 'src/tailwind/components/Button'
import { Section } from 'src/tailwind/components/Section'
import * as Table from 'src/tailwind/components/Table'
import Loader from 'src/views/components/Loader'
import NoContent from 'src/views/components/NoContent'
import { PaginationBottom, PaginationTop } from 'src/views/components/Pagination'
import UserPhoto from 'src/views/components/UserPhoto'
import SuspenseWrapper from 'src/views/includes/SuspenseWrapper'

export default function StudentPortfolioSectionPage(): JSX.Element | null {
  const t = useTranslatable()

  return (
    <SuspenseWrapper title={t('portfolio:portfolio')}>
      <PageContent />
    </SuspenseWrapper>
  )
}

function PageContent(): JSX.Element | null {
  const headers = useAuthenticatedHeaders()
  const t = useTranslatable()
  const { id } = useParams()
  const locale = useLocale()
  const theme = useTheme()
  const [searchParams] = useSearchParams({
    page: '1',
    perPage: '10',
  })

  const section = useApi({
    endpoint: Api.getStudentPortfolioSection,
    params: React.useMemo(
      () => ({
        headers,
        args: {
          id: id!,
        },
      }),
      [headers, id]
    ),
  })
  const page = searchParams.get('page')!
  const perPage = searchParams.get('perPage')!

  const {
    data: works,
    isValidating: worksPending,
    error: worksError,
  } = useApi({
    endpoint: Api.getStudentPortfolioSectionWorks,
    params: React.useMemo(
      () => ({
        headers,
        args: {
          id: section.data?.workType,
        },
        query: {
          page: parseInt(page),
          perPage: parseInt(perPage),
        },
      }),
      [headers, section.data?.workType, page, perPage]
    ),
    suspense: false,
    shouldFetch: section.data != null,
  })
  const error = worksError as any as CommonError
  const statusIcons = {
    STATUS_PENDING: <CoursePending />,
    STATUS_EVALUATED: <CourseCompletedIcon />,
    STATUS_REJECTED: <CourseFailedIcon />,
    STATUS_DRAFT: <Draft />,
  }
  const statusTexts = {
    STATUS_PENDING: t('portfolio:status_pending'),
    STATUS_EVALUATED: t('portfolio:status_evaluated'),
    STATUS_REJECTED: t('portfolio:status_rejected'),
    STATUS_DRAFT: t('portfolio:status_draft'),
  }
  return (
    <Section>
      {section.data.manage?.canSubmitWork && (
        <div className="!mb-9 flex items-center justify-end">
          <Link to="add">
            <Button variant="blue" className="flex items-center">
              <Plus color="white" /> <span className="ml-2"> {t('portfolio:create_an_assignment')}</span>
            </Button>
          </Link>
        </div>
      )}
      {works != null && works.data.length > 0 ? (
        <>
          <PaginationTop pagination={works!.meta.pagination} />
          <div className="float-right mb-3 text-xs text-captionColor">
            <span className="text-captionColor">{t('portfolio:assignmentLimit')}: </span>
            <span
              className="ml-2 text-captionColor"
              data-testid="assignmentLimit"
            >{`${section.data.sectionWorkProgress?.submitted} / ${section.data.sectionWorkProgress?.allowed}`}</span>
          </div>
          <div className="w-full py-3">
            {worksPending ? (
              <Loader className="flex" style={{ margin: 'auto' }} />
            ) : (
              <Table.Table className="mt-4">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th />
                    <Table.Th>{t('portfolio:assignment_id')}</Table.Th>
                    <Table.Th>{t('portfolio:competence')}</Table.Th>
                    <Table.Th>{t('portfolio:assignment_mentor')}</Table.Th>
                    <Table.Th>{t('common:date')}</Table.Th>
                    <Table.Th>{t('common:status')}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {works.data.map((work) => (
                    <Table.Tr key={work.id} data-testid={`work/${work.id}`}>
                      <Table.Td className="px-5 text-center align-middle">
                        <div data-testid="workStatus">
                          {statusIcons[work.status] && (
                            <div data-tooltip-id={`${work.id}-statusTooltip`}>{statusIcons[work.status]}</div>
                          )}
                          <Tooltip
                            id={`${work.id}-statusTooltip`}
                            place="top"
                            variant={theme === 'dark' ? 'dark' : 'light'}
                            className="mb-1 p-1"
                          >
                            <p className="mb-0">{statusTexts[work.status] || ''}</p>
                          </Tooltip>
                        </div>
                      </Table.Td>
                      <Table.Td>
                        <Link to={`/${locale}/student/portfolio/sections/${section.data.id}/works/${work.id}`}>
                          <span>{work.id}</span>
                        </Link>
                      </Table.Td>
                      <Table.Td data-testid="workCompetencies">
                        {work.competencies?.slice(0, 2).map((competency, index, array) => (
                          <span key={competency.id}>
                            {(index !== 0 ? ', ' : '') + competency.name}
                            {index === array.length - 1 ? '...' : ''}
                          </span>
                        ))}
                      </Table.Td>
                      <Table.Td className="flex items-center whitespace-nowrap pr-4 align-middle">
                        <UserPhoto user={work.mentor} width="39px" height="39px" />{' '}
                        <Link
                          to={`/${locale}/users/${work.mentor?.uid}`}
                          className="ml-2 whitespace-nowrap align-middle hover:underline"
                          data-tooltip-id={`${work.id}-lecturer`}
                        >
                          <span className="ml-2">{work.mentor?.fullName}</span>
                        </Link>
                      </Table.Td>
                      <Table.Td data-testid="workCreatedAt">{work.createdAt}</Table.Td>
                      <Table.Td data-testid="workStatusText">{statusTexts[work.status] || ''}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table.Table>
            )}
          </div>

          <PaginationBottom pagination={works!.meta.pagination} />
        </>
      ) : worksPending ? (
        <Loader className="flex" style={{ margin: 'auto' }} />
      ) : worksError ? (
        <NoContent
          header={t('error:an_error_occurred')}
          image={ErrorIcon}
          errors={error.type === 'ErrorsObject' ? errorsToArray(error.errors) : []}
        />
      ) : (
        <NoContent header={t('error:records_not_found')} image={Warning} />
      )}
    </Section>
  )
}
